import React, { useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { 
  Search, 
  Clock, 
  Target, 
  MessageSquare, 
  Calendar,
  CheckCircle,
  Sparkles,
  FolderOpen,
  MapPin,
  Eye,
  Send,
  User,
  Users,
  Briefcase,
  Mail,
  ArrowRight
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useSearchStore } from '../store/searchStore';
import { WaitlistModal } from '../components/WaitlistModal';
import { SignInModal } from '../components/SignInModal';
import { FeedbackModal } from '../components/FeedbackModal';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { setSearchParams } = useSearchStore();
  const [waitlistOpen, setWaitlistOpen] = React.useState(false);
  const [signInOpen, setSignInOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [heroQuery, setHeroQuery] = React.useState('Dentists');
  const [heroLocation, setHeroLocation] = React.useState('McLean, VA, USA');

  return (
    <>
      <Helmet>
        <title>Lead Pilot - Find Local Businesses with Weak Digital Presence</title>
        <meta name="description" content="Automate lead generation for freelancers and agencies. Find local businesses that need your services with AI-powered analysis." />
        <meta name="keywords" content="lead generation, digital marketing, SEO analysis, business discovery, marketing automation, local business" />
        <meta property="og:title" content="Lead Pilot - Automated Lead Generation Platform" />
        <meta property="og:description" content="Find businesses with weak digital presence and turn them into leads. Automated analysis and AI-powered pitches." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Top nav with logo */}
        <nav className="absolute top-0 left-0 right-0 z-30 px-4 py-4 md:px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 text-white hover:text-white/90 transition-colors"
            >
              <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="text-xl font-black tracking-tight">
                Lead<span className="text-yellow-300">Pilot</span>
              </span>
            </Link>
            {!isAuthenticated && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSignInOpen(true)}
                  className="text-sm font-bold text-white hover:text-white/90 transition-colors drop-shadow-sm"
                >
                  Sign in
                </button>
                <button
                  onClick={() => setWaitlistOpen(true)}
                  className="px-4 py-2 bg-white text-purple-700 text-sm font-bold rounded-lg hover:bg-gray-50 transition-colors shadow-lg"
                >
                  Join the Waitlist
                </button>
              </div>
            )}
            {isAuthenticated && (
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm font-medium text-white/90 hover:text-white transition-colors"
              >
                Go to Dashboard
              </button>
            )}
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex flex-col justify-center items-center overflow-hidden bg-gradient-to-br from-purple-600 via-purple-500 to-purple-400">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }} />
          </div>
          
          {/* Gradient orbs */}
          <motion.div 
            className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full opacity-30 blur-[100px] bg-purple-300"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          <motion.div 
            className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full opacity-20 blur-[120px] bg-indigo-600 mix-blend-multiply"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
          />
          
          <div className="container mx-auto px-4 z-10 pt-24 pb-20 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto space-y-6 text-center"
            >
              {/* Pill Label */}
              <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-2">
                <span className="text-xs md:text-sm font-semibold text-purple-50 tracking-wide uppercase">
                  AI-Powered Analysis
                </span>
              </div>
              
              {/* Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight drop-shadow-sm">
                Find local leads and their biggest <br className="hidden md:block" />
                <span className="text-yellow-300">growth gaps</span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg md:text-xl text-purple-100 font-medium max-w-2xl mx-auto">
                Search by niche + city. We detect website, socials, SEO, and conversion issues.
              </p>
              
              {/* Hero Search Bar */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (isAuthenticated) {
                    setSearchParams(heroQuery.trim(), heroLocation.trim(), {});
                    navigate('/search');
                  } else {
                    setWaitlistOpen(true);
                  }
                }}
                className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto pt-4"
              >
                <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-white/95 rounded-xl shadow-lg border border-white/20">
                  <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    value={heroQuery}
                    onChange={(e) => setHeroQuery(e.target.value)}
                    placeholder="e.g. Dentists"
                    className="flex-1 bg-transparent outline-none text-slate-800 font-medium placeholder:text-slate-400"
                  />
                </div>
                <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-white/95 rounded-xl shadow-lg border border-white/20">
                  <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    value={heroLocation}
                    onChange={(e) => setHeroLocation(e.target.value)}
                    placeholder="McLean, VA, USA"
                    className="flex-1 bg-transparent outline-none text-slate-800 font-medium placeholder:text-slate-400"
                  />
                </div>
                <button
                  type="submit"
                  className="px-8 py-3 bg-white text-purple-700 hover:bg-gray-50 rounded-xl shadow-xl font-bold transition-all hover:scale-105 flex items-center justify-center gap-2"
                >
                  <Search className="w-5 h-5" />
                  Find Leads
                </button>
              </form>
              
              {/* Bullet Trust Signals */}
              <p className="text-purple-100/90 text-sm font-medium">
                Instant report • No spam • Export to CSV
              </p>
              
              {/* Secondary CTA */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-2">
                {isAuthenticated ? (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="text-white font-semibold text-sm hover:text-white/90 transition-colors"
                  >
                    Go to Dashboard
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setSignInOpen(true)}
                      className="text-white font-bold text-sm hover:text-white/90 transition-colors"
                    >
                      Sign in
                    </button>
                    <button
                      onClick={() => setWaitlistOpen(true)}
                      className="px-5 py-2.5 bg-white text-purple-700 text-sm font-bold rounded-xl shadow-lg hover:bg-gray-50 hover:scale-105 transition-all"
                    >
                      Join the Waitlist
                    </button>
                  </>
                )}
              </div>
            </motion.div>
            
            {/* Sample Lead Report Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block absolute right-4 xl:right-8 top-1/2 -translate-y-1/2 w-[280px] z-20"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 transform -rotate-2">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Sample Lead Report</div>
                <div className="font-bold text-slate-900 text-lg mb-1">Smile Bright Dental</div>
                <div className="text-sm text-slate-500 mb-3">smilebrightdental.com</div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl font-black text-indigo-600">82/100</span>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">Opportunity: High</span>
                </div>
                <div className="text-xs font-bold text-slate-600 mb-2">Issues:</div>
                <ul className="text-xs text-slate-600 space-y-1 mb-4">
                  <li>• No WhatsApp contact button</li>
                  <li>• Slow mobile loading</li>
                  <li>• Instagram inactive 21 days</li>
                </ul>
                <button
                  type="button"
                  onClick={() => isAuthenticated ? navigate('/search') : setWaitlistOpen(true)}
                  className="w-full py-2 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Generate Outreach
                </button>
              </div>
            </motion.div>
            
            {/* Sample card - visible on mobile (below content) */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="lg:hidden mt-8 max-w-[280px] mx-auto"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-5">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Sample Lead Report</div>
                <div className="font-bold text-slate-900 text-lg mb-1">Smile Bright Dental</div>
                <div className="text-sm text-slate-500 mb-3">smilebrightdental.com</div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl font-black text-indigo-600">82/100</span>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">Opportunity: High</span>
                </div>
                <div className="text-xs font-bold text-slate-600 mb-2">Issues:</div>
                <ul className="text-xs text-slate-600 space-y-1 mb-4">
                  <li>• No WhatsApp contact button</li>
                  <li>• Slow mobile loading</li>
                  <li>• Instagram inactive 21 days</li>
                </ul>
                <button
                  type="button"
                  onClick={() => isAuthenticated ? navigate('/search') : setWaitlistOpen(true)}
                  className="w-full py-2 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Generate Outreach
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Problem Section */}
        <ProblemSection />

        {/* Solution Section */}
        <SolutionSection />

        {/* How It Works Section */}
        <HowItWorksSection />

        {/* Who It's For Section */}
        <WhoItsForSection />

        {/* Waitlist CTA Section */}
        <WaitlistCTASection onOpenWaitlist={() => setWaitlistOpen(true)} />

        {/* Footer */}
        <FooterSection onFeedbackClick={() => setFeedbackOpen(true)} />
      </div>

      <WaitlistModal
        isOpen={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        onSignIn={() => setSignInOpen(true)}
      />
      <SignInModal
        isOpen={signInOpen}
        onClose={() => setSignInOpen(false)}
        onRequestInvite={() => setWaitlistOpen(true)}
      />
      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
};

// Problem Section Component
const ProblemSection: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const problems = [
    {
      icon: Clock,
      title: 'Manual Google Maps research is tedious',
      description: 'Hours spent scrolling through listings, opening websites one by one, checking social media profiles. The time drain adds up fast, leaving less time for actual client work.',
      align: 'left'
    },
    {
      icon: Target,
      title: 'Hard to know which businesses actually need help',
      description: 'Not every business with a weak website is a good prospect. You waste time reaching out to businesses that aren\'t interested, don\'t have budget, or already hired someone.',
      align: 'right'
    },
    {
      icon: MessageSquare,
      title: 'Generic pitches don\'t convert',
      description: 'Mass emails and cookie-cutter messages get ignored. Low response rates mean you need to send even more outreach just to land one client.',
      align: 'left'
    },
    {
      icon: Calendar,
      title: 'Outreach takes too much time',
      description: 'Personalizing every message, following up, tracking conversations across emails and notes. The manual work creates a bottleneck that limits how many clients you can reach.',
      align: 'right'
    }
  ];

  return (
    <section className="py-24 md:py-32 relative overflow-hidden bg-purple-50/30">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(45deg, #7c3aed, #7c3aed 10px, transparent 10px, transparent 20px)`,
        }} />
      </div>
      
      <div className="relative z-10 max-w-6xl mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-24"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-purple-100 border border-purple-200 text-purple-700 text-sm font-semibold mb-4">
            The challenge
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            Why finding clients is so frustrating
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            If you're like most freelancers and agencies, you spend more time hunting for leads than actually doing the work you love.
          </p>
        </motion.div>
        
        <div className="space-y-16 md:space-y-24 max-w-5xl mx-auto">
          {problems.map((problem, index) => (
            <ProblemCard key={index} problem={problem} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ProblemCard: React.FC<{ problem: any; index: number }> = ({ problem, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const Icon = problem.icon;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: problem.align === 'left' ? -50 : 50 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`flex flex-col md:flex-row gap-8 items-center ${
        problem.align === 'right' ? 'md:flex-row-reverse' : ''
      }`}
    >
      <div className="flex-shrink-0">
        <div className="w-20 h-20 rounded-2xl bg-purple-100 border border-purple-200 flex items-center justify-center shadow-md">
          <Icon className="w-10 h-10 text-purple-600" />
        </div>
      </div>
      
      <div className={`flex-1 ${problem.align === 'right' ? 'md:text-right' : ''}`}>
        <h3 className="text-xl font-bold text-gray-900 mb-3">
          {problem.title}
        </h3>
        <p className="text-lg text-gray-600 leading-relaxed">
          {problem.description}
        </p>
      </div>
    </motion.div>
  );
};

// Solution Section Component
const SolutionSection: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const solutions = [
    {
      icon: Search,
      title: 'Finds local businesses with weak digital presence',
      description: 'Automated discovery that scans your target area and industry, identifying businesses with missing websites, poor SEO, weak social media, or low review counts.',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: CheckCircle,
      title: 'Shows what\'s missing',
      description: 'Clear insights into each business\'s digital gaps - website quality, SEO performance, social media presence, and review management. Know exactly what to offer.',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: Sparkles,
      title: 'Helps generate personalized outreach messages',
      description: 'AI-assisted pitch generation based on each business\'s specific needs. Create compelling, personalized messages that show you understand their challenges.',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: FolderOpen,
      title: 'Keeps leads organized in one place',
      description: 'Centralized lead management with notes, status tracking, and follow-up reminders. Never lose track of a potential client or miss a follow-up opportunity.',
      color: 'bg-amber-100 text-amber-600'
    }
  ];
  
  return (
    <section className="py-24 md:py-32 relative bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
              <div className="absolute inset-0 bg-purple-900/10 z-10" />
              <img 
                src="https://images.unsplash.com/photo-1678760873069-0c64984ea4e5"
                alt="Local business storefront representing target market"
                className="w-full h-auto object-cover"
              />
            </div>
            {/* Decorative accent */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-purple-200 rounded-full blur-2xl opacity-50 z-0"></div>
          </motion.div>
          
          {/* Right - Header */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="order-1 lg:order-2"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold mb-4">
              How Lead Pilot helps
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              Stop searching, start converting
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Lead Pilot automates the tedious work so you can focus on what matters: building relationships and delivering great results for your clients.
            </p>
          </motion.div>
        </div>
        
        {/* Solution cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {solutions.map((solution, index) => (
            <SolutionCard key={index} solution={solution} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

const SolutionCard: React.FC<{ solution: any; index: number }> = ({ solution, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const Icon = solution.icon;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative"
    >
      <div className="h-full bg-white rounded-2xl p-8 border border-purple-100 shadow-sm hover:shadow-xl hover:border-purple-300 transition-all duration-300">
        <div className={`w-16 h-16 rounded-xl ${solution.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="w-8 h-8" />
        </div>
        
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          {solution.title}
        </h3>
        
        <p className="text-gray-600 leading-relaxed">
          {solution.description}
        </p>
      </div>
    </motion.div>
  );
};

// How It Works Section
const HowItWorksSection: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const steps = [
    {
      number: '01',
      icon: MapPin,
      title: 'Search a location and business type',
      description: 'Enter your target city and industry - restaurants, retail stores, professional services, or any local business category you serve.',
      gradient: 'from-purple-500 to-indigo-500'
    },
    {
      number: '02',
      icon: Eye,
      title: 'Review opportunities and insights',
      description: 'Browse qualified leads with detailed insights about their digital presence gaps, contact info, and what services they likely need.',
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      number: '03',
      icon: Send,
      title: 'Generate a pitch and follow up',
      description: 'Create personalized outreach messages based on each business\'s specific needs, then track your conversations and follow-ups in one place.',
      gradient: 'from-purple-600 to-pink-600'
    }
  ];
  
  return (
    <section className="py-24 md:py-32 relative overflow-hidden bg-purple-50/50">
      {/* Background decoration */}
      <motion.div
        className="absolute top-1/4 left-0 w-96 h-96 rounded-full opacity-5 blur-3xl bg-purple-600"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <div className="relative z-10 max-w-6xl mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-24"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold mb-4">
            Three simple steps
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            How it works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From search to signed client in minutes, not weeks
          </p>
        </motion.div>
        
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-4 relative">
          {steps.map((step, index) => (
            <StepCard 
              key={index} 
              step={step} 
              index={index} 
              isLast={index === steps.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const StepCard: React.FC<{ step: any; index: number; isLast: boolean }> = ({ step, index, isLast }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const Icon = step.icon;
  
  return (
    <div className="relative">
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.6, delay: index * 0.2 }}
        className="relative z-10"
      >
        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-lg hover:shadow-2xl hover:border-purple-300 transition-all duration-300 h-full">
          {/* Step number badge */}
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <span className="text-5xl font-bold text-purple-100">
              {step.number}
            </span>
          </div>
          
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {step.title}
          </h3>
          
          <p className="text-gray-600 leading-relaxed">
            {step.description}
          </p>
        </div>
      </motion.div>
      
      {/* Connecting line */}
      {!isLast && (
        <div className="hidden lg:block absolute top-1/2 left-full w-full h-0.5 bg-gradient-to-r from-purple-200 to-transparent -translate-y-1/2 z-0" />
      )}
    </div>
  );
};

// Who It's For Section
const WhoItsForSection: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const audiences = [
    {
      icon: User,
      title: 'Freelancers',
      description: 'Web developers, SEO specialists, and digital marketers offering services to local businesses.',
      bg: 'bg-purple-600'
    },
    {
      icon: Users,
      title: 'Small agencies',
      description: 'Growing teams looking to scale their client acquisition without adding overhead.',
      bg: 'bg-indigo-600'
    },
    {
      icon: Briefcase,
      title: 'Consultants',
      description: 'Business consultants working with local businesses on digital transformation.',
      bg: 'bg-violet-600'
    }
  ];
  
  return (
    <section className="py-24 md:py-32 relative bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-purple-50 text-purple-600 text-sm font-semibold mb-4">
            Built for
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            Who Lead Pilot serves
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Whether you're flying solo or building a team, Lead Pilot helps you find and win more local business clients.
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {audiences.map((audience, index) => (
            <AudienceCard key={index} audience={audience} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

const AudienceCard: React.FC<{ audience: any; index: number }> = ({ audience, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const Icon = audience.icon;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative group"
    >
      <div className="h-full bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:bg-white hover:shadow-xl hover:border-purple-200 transition-all duration-300">
        <div className={`w-14 h-14 rounded-xl ${audience.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
        
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          {audience.title}
        </h3>
        
        <p className="text-gray-600 leading-relaxed">
          {audience.description}
        </p>
      </div>
    </motion.div>
  );
};

// Waitlist CTA Section
const WaitlistCTASection: React.FC<{ onOpenWaitlist: () => void }> = ({ onOpenWaitlist }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <section className="py-24 md:py-32 relative overflow-hidden bg-purple-900">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-800 via-purple-900 to-indigo-900 opacity-80" />
      
      {/* Animated orbs */}
      <motion.div
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-purple-500"
        animate={{
          scale: [1, 1.3, 1],
          rotate: [0, 90, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 md:p-12 lg:p-16 border border-white/10 shadow-2xl">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-400/30">
                <Sparkles className="w-4 h-4 text-purple-200" />
                <span className="text-sm font-semibold text-purple-100">
                  Private Beta Access
                </span>
              </div>
              
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                Ready to find your next client?
              </h2>
              
              <p className="text-xl text-purple-200 leading-relaxed max-w-2xl mx-auto">
                Get early access to Lead Pilot and help shape the product. We're in private beta and building with our users.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <button 
                  onClick={onOpenWaitlist}
                  className="bg-white text-purple-900 hover:bg-gray-100 text-lg px-10 py-6 font-bold shadow-lg hover:shadow-xl transition-all rounded-xl inline-flex items-center gap-2 justify-center"
                >
                  Join the Waitlist
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex flex-wrap items-center justify-center gap-8 pt-8 text-sm text-purple-300/80">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full" />
                  <span>Early access pricing</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Section
const FooterSection: React.FC<{ onFeedbackClick?: () => void }> = ({ onFeedbackClick }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-purple-950 border-t border-purple-900/50">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-md">
                <svg className="w-4 h-4 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="text-base md:text-lg lg:text-xl font-bold tracking-tight text-white">
                Lead<span className="text-yellow-300">Pilot</span>
              </span>
            </div>
            <p className="text-purple-200 text-sm mb-2 font-medium">
              Discover your next opportunity
            </p>
            <p className="text-purple-300/60 text-xs max-w-xs leading-relaxed">
              Stop wasting time on manual research. Identify businesses with weak digital presence instantly.
            </p>
          </div>
          
          {/* Links */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">
              Legal
            </h3>
            <nav className="space-y-3">
              <span className="block text-sm text-purple-300 hover:text-white transition-colors duration-200 cursor-pointer">
                Privacy Policy
              </span>
              <span className="block text-sm text-purple-300 hover:text-white transition-colors duration-200 cursor-pointer">
                Terms of Service
              </span>
              <span className="block text-sm text-purple-300 hover:text-white transition-colors duration-200 cursor-pointer">
                Cookie Policy
              </span>
            </nav>
          </div>
          
          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">
              Contact
            </h3>
            <div className="space-y-2">
              <a 
                href="mailto:hello@sapiensclique.com"
                className="inline-flex items-center gap-2 text-sm text-purple-300 hover:text-white transition-colors duration-200 group"
              >
                <div className="p-1.5 rounded-md bg-purple-900/50 group-hover:bg-purple-800 transition-colors">
                   <Mail className="w-4 h-4" />
                </div>
                hello@sapiensclique.com
              </a>
              {onFeedbackClick && (
                <button
                  onClick={onFeedbackClick}
                  className="block text-sm text-purple-300 hover:text-white transition-colors duration-200 text-left"
                >
                  Send feedback
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="pt-8 border-t border-purple-900/50 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-center md:text-left text-purple-400/60">
            © {currentYear} Lead Pilot. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Landing;
